import { Button, type ButtonProps } from "./Button";
import { Icon, type IconSize } from "./Icon";
import type { IconGlyph, IconWeight } from "@/icons";
import { forwardRef } from "react";

/** Square sizes of an icon-only button; `icon-bar` is the app-bar chrome (menu, back, search): an `icon-sm` box with an `xl` glyph. */
export type IconButtonSize = "icon-xs" | "icon-sm" | "icon-bar" | "icon-md" | "icon-lg";

const GLYPH_SIZE: Record<IconButtonSize, IconSize> = {
  "icon-xs": "sm",
  "icon-sm": "lg",
  "icon-bar": "xl",
  "icon-md": "lg",
  "icon-lg": "lg",
};

export type IconButtonProps = Omit<ButtonProps, "size" | "children" | "align" | "fullWidth" | "aria-label" | "tone" | "variant"> & {
  tone?: Exclude<NonNullable<ButtonProps["tone"]>, "game" | "night">;
  variant?: ButtonProps["variant"];
  icon: IconGlyph;
  /** Accessible name (`aria-label`); required because the button has no text. */
  label: string;
  size?: IconButtonSize;
  iconWeight?: IconWeight;
  /** Overrides the glyph size the box picks (a trash trigger matching a stepper's glyphs, a close X at `md`). */
  iconSize?: IconSize;
};

/** Icon-only Button whose glyph scales with the box (`icon-sm` and up draw an `lg` glyph, `icon-bar` an `xl` one); defaults to a ghost neutral circle. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, size = "icon-md", iconSize, iconWeight = "bold", variant = "ghost", tone = "neutral", shape = "circle", ...rest },
  ref,
) {
  return (
    <Button {...rest} ref={ref} variant={variant} tone={tone} shape={shape} size={size} aria-label={label}>
      <Icon as={icon} size={iconSize ?? GLYPH_SIZE[size]} weight={iconWeight} />
    </Button>
  );
});
